import { Component, input, output, computed, signal, effect, viewChild, ElementRef, afterRenderEffect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkTableModule } from '@angular/cdk/table';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { TranslateModule } from '@ngx-translate/core';

export interface TableColumn {
  key: string;
  label: string;
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatSortModule,
    CdkTableModule,
    DragDropModule,
    TranslateModule
  ],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent {
  data = input<any[]>([]);
  columns = input<TableColumn[]>([]);
  searchTerm = input<string | { columnKey: string; searchTerm: string } | undefined>();
  clickable = input<boolean>(false);

  // Highlight / scroll-to
  highlightedRowKey = input<string>('name');
  highlightedRowValue = input<unknown | null>(null);
  
  // Pagination (optionnelle)
  paginate = input<boolean>(false);
  pageSize = input<number>(10);
  pageSizeOptions = input<number[]>([5, 10, 25, 50, 100]);

  // Output pour émettre la ligne sélectionnée
  selectionLigne = output<any>();

  columnOrder = signal<string[]>([]);
  sort = viewChild(MatSort);
  tableContainer = viewChild<ElementRef>('tableContainer');
  
  sortedData = signal<any[]>([]);
  
  // État de la pagination
  pageIndex = signal<number>(0);
  currentPageSize = signal<number>(10);

  constructor() {
    effect(() => {
      this.columnOrder.set(this.columns().map(col => col.key));
    });
    
    // Initialiser les données triées
    effect(() => {
      this.sortedData.set(this.filteredData());
    });
    
    // Initialiser la taille de page depuis l'input
    effect(() => {
      this.currentPageSize.set(this.pageSize());
    });

    afterRenderEffect(() => {
      const value     = this.highlightedRowValue();
      const data      = this.paginatedData();
      const container = this.tableContainer();
      if (!value || !data.length || !container) return;
      this.scrollToHighlighted();
    });
  }

  displayedColumns = computed(() => 
    this.columnOrder()
  );

  // Données paginées (si pagination activée)
  paginatedData = computed(() => {
    const data = this.sortedData();
    
    if (!this.paginate()) {
      return data;
    }
    
    const startIndex = this.pageIndex() * this.currentPageSize();
    const endIndex = startIndex + this.currentPageSize();
    return data.slice(startIndex, endIndex);
  });

  // Nombre total d'éléments (pour le paginator)
  totalItems = computed(() => this.sortedData().length);

  filteredData = computed(() => {
    const filter = this.searchTerm();
    let allData = [...this.data()];

    if (!filter) return allData;

    if (typeof filter === 'string') {
      const searchValue = filter.trim().toLowerCase();
      if (searchValue !== '') {
        return allData.filter(row =>
          this.columns().some(column => {
            const cellValue = row[column.key];
            return cellValue != null && String(cellValue).toLowerCase().includes(searchValue);
          })
        );
      }
    } else if (filter.columnKey && filter.searchTerm?.trim()) {
      return allData.filter(row => {
        const cellValue = row[filter.columnKey];
        return cellValue != null && 
               String(cellValue).toLowerCase().includes(filter.searchTerm.toLowerCase());
      });
    }

    return allData;
  });
    
  getCellValue(row: any, columnKey: string): any {
    return row[columnKey] ?? '-';
  }

  isHighlightedRow(row: any): boolean {
    const key = this.highlightedRowKey();
    const value = this.highlightedRowValue();
    return key != null && value != null && row[key] === value;
  }

  // MatSort gère le tri automatiquement
  onSortChange(sortState: Sort): void {
    const data = [...this.filteredData()];
    
    if (!sortState.active || sortState.direction === '') {
      this.sortedData.set(data);
      return;
    }

    const sorted = data.sort((a, b) => {
      const aValue = a[sortState.active];
      const bValue = b[sortState.active];
      
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = this.compareValues(aValue, bValue);
      return sortState.direction === 'asc' ? comparison : -comparison;
    });

    this.sortedData.set(sorted);
  }

  private compareValues(a: any, b: any): number {
    const aNum = typeof a === 'number' || !isNaN(Number(a));
    const bNum = typeof b === 'number' || !isNaN(Number(b));
    if (aNum && bNum) return Number(a) - Number(b);
    if (aNum) return -1;
    if (bNum) return 1;
    return String(a).localeCompare(String(b));
  }

  drop(event: CdkDragDrop<string[]>): void {
    const columns = [...this.columnOrder()];
    moveItemInArray(columns, event.previousIndex, event.currentIndex);
    this.columnOrder.set(columns);
  }

  // Gestion du clic sur une ligne
  onRowClick(row: any): void {
    if (this.clickable()) {
      this.selectionLigne.emit(row);
    }
  }

  // Gestion du changement de page
  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.currentPageSize.set(event.pageSize);
  }

  scrollToHighlighted(): void {
    const key   = this.highlightedRowKey();
    const value = this.highlightedRowValue();
    if (!key || value == null) return;

    const container = this.tableContainer()?.nativeElement as HTMLElement | undefined;
    if (!container) return;
    const rowEl = Array.from(container.querySelectorAll<HTMLElement>('tr[data-row-key]'))
      .find(el => el.getAttribute('data-row-key') === String(value));

    if (!rowEl) return;

    const containerRect = container.getBoundingClientRect();
    const rowRect       = rowEl.getBoundingClientRect();
    const rowOffsetTop  = rowRect.top - containerRect.top + container.scrollTop;
    const scrollTop     = rowOffsetTop - container.clientHeight / 2 + rowEl.offsetHeight / 2;

    container.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }
}
